import { type ComponentPropsWithoutRef, type ElementType } from 'react';
import clsx from 'clsx';

export interface PanelShellProps<T extends ElementType = 'div'> {
  /** Rendered element, so a header can stay a `<header>` and a canvas a `<section>`. */
  as?: T;
  className?: string;
  children?: ComponentPropsWithoutRef<T>['children'];
}

/**
 * The one panel surface every Content Studio panel shares: white fill, ~12px radius, no
 * border and no shadow. What separates two panels is the page's grey showing through the
 * gap between them, so a border would draw the same division twice.
 */
export function PanelShell<T extends ElementType = 'div'>({
  as,
  className,
  children,
  ...rest
}: PanelShellProps<T> & Omit<ComponentPropsWithoutRef<T>, keyof PanelShellProps<T>>) {
  // Not UI copy - the fallback HTML tag name for the polymorphic `as` prop.
  // eslint-disable-next-line i18next/no-literal-string
  const Component = as ?? 'div';

  return (
    <Component className={clsx('rounded-xl bg-background', className)} {...rest}>
      {children}
    </Component>
  );
}
